import * as preact from "preact";
import Sortable from "sortablejs";

import * as deviceDetect from "deviceDetect";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import * as buttonRole from "ui/buttonRole";
import MiniMarkdownView from "views/MiniMarkdownView";

interface RecentPrompt {
  prompt: string;
  _id: string;
  pinned: boolean;
}

interface Props {
  userWord: UserWordStore;
  context: string;
}

interface State {
  askAIResponse?: string;
  askAIRequest?: string;
  askAIFetching?: boolean;

  recentPrompts?: RecentPrompt[];
  showRecentPrompts?: boolean;
  editing?: boolean;
}

class AskAIView extends preact.Component<Props, State> {
  inputRow: HTMLDivElement;
  inputContainer: HTMLDivElement;
  recentPromptsList: HTMLUListElement;
  recentPromptsSortable?: Sortable;

  constructor(props: Props) {
    super(props);

    this.state = {};

    this.fetchRecentPrompts();
    window.document.addEventListener("click", this.onDocumentClick);
  }

  componentDidMount(): void {
    this.setupRecentPromptsSortable();
  }

  componentDidUpdate(previousProps: Props): void {
    // A question and its answer are about one word in one context (the
    // sidebar keeps this view as the next word is looked up)
    if (!this.isAbout(previousProps)) {
      this.setState({
        askAIRequest: "",
        askAIResponse: undefined,
        askAIFetching: false,
      });
    }
    this.setupRecentPromptsSortable();
  }

  /** Whether the word and context are still those given */
  isAbout({ userWord, context }: Props) {
    return userWord === this.props.userWord && context === this.props.context;
  }

  componentWillUnmount(): void {
    window.document.removeEventListener("click", this.onDocumentClick);
    this.recentPromptsSortable?.destroy();
  }

  onDocumentClick = (event) => {
    if (
      this.state.showRecentPrompts &&
      this.inputContainer &&
      $.contains(this.inputContainer, event.target)
    ) {
      // Don't do anything in this case, since the user is interacting with the recent prompts
      return;
    }

    this.setState({ showRecentPrompts: false, editing: false });
  };

  async fetchRecentPrompts() {
    const recentPrompts = (
      await readlangClient.request(
        "GET",
        "/api/smartDictionary/recentAskAIPrompts",
        {
          language: this.props.userWord.state.value.language,
        }
      )
    ).data.prompts;

    this.setState({ recentPrompts });
  }

  async togglePin(prompt: RecentPrompt) {
    const newPrompts = (
      await readlangClient.request(
        "POST",
        `/api/smartDictionary/askAIPrompt/${prompt._id}/pin`,
        {
          value: !prompt.pinned,
        }
      )
    ).data.prompts;
    this.setState({ recentPrompts: newPrompts });
  }

  async deletePrompt(prompt: RecentPrompt) {
    const newPrompts = (
      await readlangClient.request(
        "DELETE",
        `/api/smartDictionary/askAIPrompt/${prompt._id}`,
        {}
      )
    ).data.prompts;
    this.setState({ recentPrompts: newPrompts });
  }

  private setupRecentPromptsSortable() {
    this.recentPromptsSortable?.destroy();
    this.recentPromptsSortable = undefined;

    if (!this.state.editing || !this.recentPromptsList) {
      return;
    }

    this.recentPromptsSortable = Sortable.create(this.recentPromptsList, {
      animation: 150,
      draggable: ".promptRow.pinned",
      handle: ".dragHandle",
      onEnd: this.syncPinnedPromptsFromDom,
      onMove: (event) =>
        !event.related || event.related.classList.contains("pinned"),
    });
  }

  private promptIdsFromElement(element?: HTMLUListElement | null) {
    return Array.from(
      element?.querySelectorAll<HTMLLIElement>(
        "li.promptRow.pinned[data-prompt-id]"
      ) ?? []
    )
      .map((item) => item.getAttribute("data-prompt-id"))
      .filter((id): id is string => Boolean(id));
  }

  private syncPinnedPromptsFromDom = () => {
    const reordered = this.promptIdsFromElement(this.recentPromptsList);
    void this.reorderPinned(reordered);
  };

  async reorderPinned(reordered: string[]) {
    const prompts = this.state.recentPrompts ?? [];
    const pinnedIds = prompts.filter((p) => p.pinned).map((p) => p._id);
    if (
      reordered.length !== pinnedIds.length ||
      reordered.some((id) => !pinnedIds.includes(id)) ||
      reordered.every((id, index) => id === pinnedIds[index])
    ) {
      return;
    }

    // Optimistically update UI by reordering pinned prompts in state
    const pinnedById = new Map(
      prompts.filter((p) => p.pinned).map((p) => [p._id, p])
    );
    const optimisticPinned = reordered.map((id) => pinnedById.get(id)!);
    const unpinned = prompts.filter((p) => !p.pinned);
    this.setState({ recentPrompts: [...optimisticPinned, ...unpinned] });

    const newPrompts = (
      await readlangClient.request(
        "POST",
        "/api/smartDictionary/askAIPrompt/reorderPinned",
        {
          language: this.props.userWord.state.value.language,
          orderedIds: reordered,
        }
      )
    ).data.prompts;
    this.setState({ recentPrompts: newPrompts });
  }

  recentPromptsStyle() {
    const editCardInset = 10;
    if (!this.inputRow || !deviceDetect.isSmall()) {
      return undefined;
    }

    const inputRowRect = this.inputRow.getBoundingClientRect();
    const containingElement = this.inputRow.closest(
      ".editCardTable, .editCardPanel"
    );
    const boundaryRect = containingElement?.getBoundingClientRect();
    const boundaryLeft = (boundaryRect?.left ?? 0) + editCardInset;
    const scale = inputRowRect.width / this.inputRow.offsetWidth || 1;
    const leftOffset = Math.max(0, inputRowRect.left - boundaryLeft) / scale;
    const width = (inputRowRect.right - boundaryLeft) / scale;

    return {
      boxSizing: "border-box",
      left: `-${leftOffset}px`,
      right: "auto",
      width: `${width}px`,
    };
  }

  render() {
    const { recentPrompts, showRecentPrompts, editing } = this.state;

    return (
      <div class="askAIView">
        <div
          class="inputRow"
          ref={(element) => {
            this.inputRow = element;
          }}
        >
          <div
            class="inputContainer"
            onMouseEnter={() => {
              if (!editing) {
                this.setState({ showRecentPrompts: true });
              }
            }}
            onMouseLeave={() => {
              if (!editing) {
                this.setState({ showRecentPrompts: false });
              }
            }}
            ref={(element) => {
              this.inputContainer = element;
            }}
          >
            <input
              type="text"
              placeholder={`Ask AI about this ${this.props.userWord.isPhrase() ? "phrase" : "word"}`}
              onInput={(event) => {
                this.setState({
                  askAIRequest: event.currentTarget.value,
                  showRecentPrompts: false,
                  editing: false,
                });
              }}
              value={this.state.askAIRequest}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  this.askAI();
                }
              }}
              onFocus={() => {
                this.setState({ showRecentPrompts: true });
              }}
            />
            {recentPrompts?.length > 0 && showRecentPrompts ? (
              <div
                class={`recentPrompts${editing ? " editing" : ""}`}
                style={this.recentPromptsStyle()}
              >
                <div class="recentPromptsHeader">
                  <h3>Reuse recent prompt</h3>
                  <button
                    class="editToggle"
                    type="button"
                    onClick={() => this.setState({ editing: !editing })}
                  >
                    {editing ? "Done" : "Edit"}
                  </button>
                </div>
                <ul
                  ref={(element) => {
                    this.recentPromptsList = element;
                  }}
                >
                  {recentPrompts.map((prompt) => {
                    return (
                      <li
                        class={`promptRow${prompt.pinned ? " pinned" : ""}`}
                        data-prompt-id={prompt._id}
                        key={prompt._id}
                      >
                        {editing && prompt.pinned ? (
                          <div class="dragHandle" title="Drag to reorder">
                            <i class="icon-align-justify" />
                          </div>
                        ) : null}
                        <div
                          class={`pinButton${prompt.pinned ? " pinned" : ""}${editing ? " interactive" : ""}`}
                          onClick={
                            editing ? () => this.togglePin(prompt) : undefined
                          }
                          aria-label={
                            editing
                              ? prompt.pinned
                                ? "Unpin prompt"
                                : "Pin prompt"
                              : undefined
                          }
                          {...(editing ? buttonRole.props : {})}
                        >
                          <i class="icon-pushpin" />
                        </div>
                        <div
                          class="prompt"
                          onClick={async () => {
                            if (editing) return;
                            await this.setState({
                              askAIRequest: prompt.prompt,
                              showRecentPrompts: false,
                            });
                            this.askAI();
                          }}
                        >
                          {prompt.prompt}
                        </div>
                        {editing ? (
                          <button
                            type="button"
                            class="deleteButton"
                            aria-label="Delete prompt"
                            onClick={() => this.deletePrompt(prompt)}
                          >
                            <i class="icon-remove" />
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
          <button onClick={() => this.askAI()}>Ask&nbsp;AI</button>
        </div>
        <p class="aiResponse">
          {this.state.askAIFetching ? (
            "Fetching..."
          ) : this.state.askAIResponse ? (
            <MiniMarkdownView text={this.state.askAIResponse} />
          ) : null}
        </p>
      </div>
    );
  }

  renderAIResponse() {
    return;
  }

  upgrade() {
    premiumUpsellModal.show({
      title: "askAI",
      heading: "Upgrade to Ask AI",
      subheading:
        "Ask questions related to new vocabulary: grammar, synonyms, culture, or whatever you are curious about!",
      showTitle: false,
      smartDefinitions: true,
    });
  }

  async askAI() {
    if (!stores.user.hasPremiumAccess.value) {
      this.upgrade();
      return;
    }

    this.setState({
      askAIFetching: true,
      showRecentPrompts: false,
      editing: false,
    });
    const askedAbout = this.props;
    readlangClient.request(
      "GET",
      "/api/smartDictionary/askAI",
      {
        language: this.props.userWord.state.value.language,
        word: this.props.userWord.state.value.word,
        context: this.props.context,
        prompt: this.state.askAIRequest,
      },
      (explanation) => {
        // Too late: they've moved on to another word
        if (!this.isAbout(askedAbout)) {
          return;
        }

        // If the explanation starts and ends with quotes, remove them
        // (not doing on server since it's streaming the response, so we don't know what the last
        // character is until the end of the response)
        if (
          explanation[0] === '"' &&
          explanation[explanation.length - 1] === '"'
        ) {
          explanation = explanation.slice(1, -1);
        }

        this.setState({
          askAIResponse: explanation,
          askAIFetching: false,
          showRecentPrompts: false,
        });
      },
      undefined,
      { dataType: "text" }
    );
  }
}

export default AskAIView;
